import React, { useState, useEffect } from "react";
import { PencilLine, Send, Sparkles, ShieldCheck, ShieldAlert, Ban, X, Hash, ArrowRight, Copy, ClipboardCheck, Cpu, Globe, MapPin, Timer, Lock } from "lucide-react";

export const ACTION_META = {
  ALLOW: { label: "Allow", color: "#10B981", bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.35)" },
  WARN: { label: "Warn & Log", color: "#F59E0B", bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.35)" },
  MASK: { label: "Mask", color: "#06B6D4", bg: "rgba(6,182,212,0.10)", border: "rgba(6,182,212,0.35)" },
  BLOCK: { label: "Block", color: "#F43F5E", bg: "rgba(244,63,94,0.10)", border: "rgba(244,63,94,0.35)" },
  VERIFIED: { label: "Verified", color: "#10B981", bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.35)" },
  UNVERIFIED: { label: "Unverified", color: "#F59E0B", bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.35)" },
};

export function ActionBadge({ action, small, testid }) {
  const m = ACTION_META[action] || ACTION_META.ALLOW;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full font-mono font-semibold uppercase tracking-wider ${
        small ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs"
      }`}
      style={{ color: m.color, background: m.bg, border: `1px solid ${m.border}` }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.color }} />
      {m.label}
    </span>
  );
}

export function Card({ children, className = "", ...rest }) {
  return (
    <div
      className={`rounded-xl border border-subtle bg-surface/90 backdrop-blur-md ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Pill({ children, tone = "brand", className = "" }) {
  const tones = {
    brand: "text-brand border-brand/40 bg-brand/10",
    eu: "text-eu border-eu/40 bg-eu/10",
    gold: "text-gold border-gold/40 bg-gold/10",
    muted: "text-slate-400 border-subtle bg-white/5",
    warn: "text-warn border-warn/40 bg-warn/10",
    allow: "text-allow border-allow/40 bg-allow/10",
    mask: "text-mask border-mask/40 bg-mask/10",
    block: "text-block border-block/40 bg-block/10",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

// Shared stat/metric tile — one identical treatment across every dashboard,
// the employee view, the platform overview and the monthly report.
export function StatCard({ icon: Icon, label, value, sub, color = "#0EA5E9", valueColor, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
        {Icon && <Icon className="h-4 w-4" style={{ color }} />}
      </div>
      <div className="mt-2 font-display text-3xl font-extrabold" style={{ color: valueColor || "#F8FAFC" }}>
        {value}
      </div>
      {sub && <div className="mt-1 text-[11px] text-slate-500">{sub}</div>}
    </Card>
  );
}

export function Section({ title, subtitle, right, children }) {
  return (
    <div>
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
            {title}
          </h1>
          {subtitle && <p className="mt-1 text-sm text-slate-400">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export function Btn({ children, variant = "primary", className = "", ...rest }) {
  const styles = {
    primary:
      "bg-brand text-[#04121c] hover:bg-sky-400 font-semibold shadow-lg shadow-brand/20",
    ghost: "border border-subtle text-slate-200 hover:border-highlight hover:bg-white/5",
    danger: "border border-block/40 text-block hover:bg-block/10",
  };
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-40 ${styles[variant]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Logo({ className = "h-9" }) {
  return (
    <img
      src="/lucidgate-logo.png?v=2"
      alt="LucidGate — Human in Command"
      className={`${className} w-auto select-none`}
      draggable={false}
      data-testid="lucidgate-logo"
    />
  );
}

/* ── Data Trace ─────────────────────────────────────────────────────────────
   The canonical 4-step view of what happened to a single request, shown
   identically in Gateway Chat's Live Inspection and in the Audit Drawer:
     1. Input          — the prompt exactly as typed
     2. Sent           — the payload sent to the provider after masking
     3. Received       — the raw AI response before the verification gate
     4. Final output   — what the user can use, with its verification status
   For blocked / held requests no provider is contacted, so steps 3 & 4
   (and 2) render an explicit "not applicable" state instead of being empty. */

const fmtTs = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { hour12: false }) : "");

// Renders trace text with inline MARKING so it is instantly clear what was
// tokenized/labelled: mode "tokens" highlights [MASKED_..._n] (cyan); mode
// "entities" highlights detected sensitive values (amber, "will be tokenized");
// mode "values" highlights restored real values (green, "restored for you").
function HighlightedSegments({ text, mode, values }) {
  const t = String(text);
  if (mode === "tokens") {
    const parts = t.split(/(\[MASKED_[A-Z0-9_]+\])/g);
    return parts.map((p, i) =>
      /^\[MASKED_[A-Z0-9_]+\]$/.test(p)
        ? <span key={i} className="rounded border border-mask/50 bg-mask/20 px-1 font-semibold text-mask">{p}</span>
        : <React.Fragment key={i}>{p}</React.Fragment>
    );
  }
  const uniq = [...new Set(values || [])].filter(Boolean).sort((a, b) => b.length - a.length);
  if (!uniq.length) return t;
  const esc = uniq.map((v) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${esc.join("|")})`, "g");
  const parts = t.split(re);
  const cls = mode === "values"
    ? "rounded border border-allow/50 bg-allow/15 px-1 font-semibold text-allow"
    : "rounded border border-warn/50 bg-warn/15 px-1 font-semibold text-warn";
  return parts.map((p, i) =>
    uniq.includes(p) ? <span key={i} className={cls}>{p}</span> : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

function TraceText({ text, mode, values }) {
  return (
    <pre className="custom-scrollbar mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded-lg border border-subtle bg-elevated p-3 font-mono text-[11px] leading-relaxed text-slate-300">
      {text ? <HighlightedSegments text={text} mode={mode} values={values} /> : "—"}
    </pre>
  );
}

function TraceNA({ note }) {
  return (
    <div className="mt-2 flex items-center gap-2 rounded-lg border border-block/30 bg-block/10 px-3 py-2 text-xs text-block">
      <Ban className="h-3.5 w-3.5 shrink-0" /> not applicable — {note}
    </div>
  );
}

function TraceRow({ index, icon: Icon, label, sub, tag, children, testid }) {
  return (
    <div className="rounded-xl border border-subtle bg-surface/60 p-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-brand/40 bg-brand/10 text-brand">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Step {index}</div>
          <div className="font-display text-sm font-bold text-white">{label}</div>
        </div>
        {tag}
      </div>
      <div className="pl-1">{children}</div>
      {sub && <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-slate-600">{sub}</div>}
    </div>
  );
}

export function DataTrace({ trace, testid = "data-trace" }) {
  const { input, sent, received, final, streaming, entities = [] } = trace;
  return (
    <div data-testid={testid} className="space-y-3">
      <TraceRow index={1} icon={PencilLine} label="Input" sub="Prompt exactly as typed" testid="trace-step-input">
        <TraceText text={input} mode="entities" values={entities} />
      </TraceRow>

      <TraceRow
        index={2}
        icon={Send}
        label="Sent"
        sub="Payload the AI provider actually received"
        testid="trace-step-sent"
        tag={sent.applicable && sent.unchanged ? (
          <span data-testid="trace-sent-unchanged" className="ml-auto rounded-full border border-allow/40 bg-allow/10 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-allow">
            sent unchanged
          </span>
        ) : null}
      >
        {sent.applicable ? <TraceText text={sent.text} mode={sent.unchanged ? undefined : "tokens"} /> : <TraceNA note={sent.note} />}
      </TraceRow>

      <TraceRow index={3} icon={Sparkles} label="Received" sub="Raw AI response before the verification gate" testid="trace-step-received">
        {received.applicable ? (
          <TraceText text={received.text || (streaming ? "…streaming…" : "")} mode="tokens" />
        ) : (
          <TraceNA note={received.note} />
        )}
      </TraceRow>

      <TraceRow index={4} icon={ShieldCheck} label="Final output" sub="What you actually see and can use" testid="trace-step-final">
        {final.applicable ? (
          <>
            <TraceText text={final.text || (streaming ? "…streaming…" : "")} mode="values" values={entities} />
            <div className="mt-2">
              {final.status === "verified" ? (
                <span data-testid="trace-final-status" className="inline-flex items-center gap-1.5 rounded-lg border border-allow/40 bg-allow/10 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-allow">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Verified{final.verifiedBy ? ` by ${final.verifiedBy}` : ""}{final.verifiedAt ? ` · ${fmtTs(final.verifiedAt)}` : ""}
                </span>
              ) : (
                <span data-testid="trace-final-status" className="inline-flex items-center gap-1.5 rounded-lg border border-warn/40 bg-warn/10 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-warn">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  Unverified — not yet confirmed
                </span>
              )}
            </div>
          </>
        ) : (
          <TraceNA note={final.note} />
        )}
      </TraceRow>
    </div>
  );
}

// List/export audit rows no longer carry plaintext. Trace steps show withheld notes.
export function traceFromAudit(row) {
  const blocked = row.decision === "BLOCK";
  const held = !!row.held;
  const noProvider = blocked || held || !row.provider_name;
  const note = blocked ? "request blocked" : held ? "request held — no compliant provider" : "no provider contacted";
  const withheld = { applicable: true, text: "(content withheld from audit list)", withheld: true };
  const na = (n) => ({ applicable: false, note: n });
  const input = "(content withheld from audit list)";
  const sent = noProvider ? na(note) : withheld;
  const received = noProvider ? na(note) : withheld;
  const final = noProvider
    ? na(note)
    : {
        applicable: true,
        text: "(content withheld from audit list)",
        withheld: true,
        status: row.verification_status === "verified" ? "verified" : "unverified",
        verifiedBy: row.verified_by,
        verifiedAt: row.verified_at,
      };
  const meta = row.detected_entity_meta || [];
  return {
    input,
    sent,
    received,
    final,
    entities: meta
      .map((e) => e && (e.trusted_label || e.category))
      .filter(Boolean),
  };
}

// Plain-text rendering of the metadata-only trace (no plaintext prompts/responses).
export function traceToText(row) {
  const t = traceFromAudit(row);
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { hour12: false }) : "");
  const na = (s) => `not applicable — ${s.note}`;
  const sent = t.sent.applicable ? t.sent.text : na(t.sent);
  const received = t.received.applicable ? t.received.text || "—" : na(t.received);
  let final = t.final.applicable ? t.final.text || "—" : na(t.final);
  if (t.final.applicable) {
    final += t.final.status === "verified"
      ? `\n\nVerification: Verified${t.final.verifiedBy ? ` by ${t.final.verifiedBy}` : ""}${t.final.verifiedAt ? ` · ${fmt(t.final.verifiedAt)}` : ""}`
      : "\n\nVerification: Unverified — not yet confirmed";
  }
  return [
    "LucidGate — Request metadata",
    `Record: ${row.audit_hash} · ${fmt(row.timestamp)}`,
    `User / Department: ${row.user_name} · ${row.department_name || "—"}`,
    "",
    "STEP 1 — INPUT", t.input || "—", "",
    "STEP 2 — SENT", sent, "",
    "STEP 3 — RECEIVED", received, "",
    "STEP 4 — FINAL OUTPUT", final,
  ].join("\n");
}

// Shared metadata drawer. Content lives on owner conversation history only.
export function RequestDetailDrawer({ row, onClose, onContinue }) {
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { hour12: false }) : "");
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  const copyTrace = async () => {
    try { await navigator.clipboard.writeText(traceToText(row)); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) { /* noop */ }
  };
  const entities = row.detected_entity_meta || [];
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="request-detail-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="lg-rise custom-scrollbar relative h-full w-full max-w-xl overflow-y-auto border-l border-subtle bg-surface p-6">
        <div className="mb-5 flex items-start justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Request detail</div>
            <div className="flex items-center gap-2">
              <ActionBadge action={row.decision} />
              {row.monitored && <Pill tone="warn">monitored</Pill>}
              {row.held && <Pill tone="warn">held</Pill>}
            </div>
            <div className="mt-1 flex items-center gap-1.5 font-mono text-[11px] text-slate-500">
              <Hash className="h-3 w-3" /> {row.audit_hash} · {fmt(row.timestamp)}
            </div>
          </div>
          <button data-testid="detail-close" onClick={onClose} className="rounded-lg border border-subtle p-1.5 text-slate-400 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          {onContinue && row.conversation_id && (
            <button
              data-testid="continue-in-chat"
              onClick={() => onContinue(row)}
              className="flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-[#04121c] hover:bg-sky-400"
            >
              Continue in Gateway Chat <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            data-testid="detail-copy-trace"
            onClick={copyTrace}
            className="flex items-center gap-1.5 rounded-lg border border-subtle px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-brand/50 hover:text-brand"
          >
            {copied ? <ClipboardCheck className="h-3 w-3 text-allow" /> : <Copy className="h-3 w-3" />}
            {copied ? "Trace copied" : "Copy metadata"}
          </button>
        </div>

        <div className="mb-4">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">User / Department</div>
          <div className="text-sm text-slate-300">{row.user_name} · {row.department_name || "—"}</div>
        </div>

        <div className="mb-4" data-testid="detail-trace">
          <div className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <Lock className="h-3 w-3" /> Metadata only · open the conversation for content
          </div>
          <DataTrace trace={traceFromAudit(row)} testid="detail-data-trace" />
        </div>

        <div className="mb-4">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">Detected categories</div>
          {entities.length === 0 ? (
            <span className="text-xs text-allow">No sensitive categories recorded.</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {entities.map((e, i) => (
                <span key={i} className="rounded-md border border-subtle bg-elevated px-2 py-1 font-mono text-[11px] text-slate-300">
                  {e.trusted_label || e.category}
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">Routing</div>
          {row.provider_name ? (
            <div className="mt-2 flex items-center gap-2 rounded-lg border border-subtle bg-elevated p-2.5">
              <Cpu className="h-4 w-4 text-brand" />
              <span className="text-sm text-white">{row.provider_name}</span>
              {row.eu_sovereign_route && <Pill tone="eu"><Globe className="h-3 w-3" /> EU</Pill>}
              {row.provider_simulated && <Pill tone="warn">sim</Pill>}
              <span className="ml-auto flex items-center gap-1 font-mono text-[11px] text-slate-500"><Timer className="h-3 w-3" />{row.latency_ms} ms</span>
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-2 rounded-lg border border-block/30 bg-block/10 p-2.5 text-xs text-block">
              <ShieldAlert className="h-4 w-4" /> No provider was contacted.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


